import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import styles from '../../styles/SparkActionModal.module.css';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PublicIcon from '@mui/icons-material/Public';

interface DeepResearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (briefing: string, webSearch: boolean) => Promise<void>;
    initialBriefing: string;
    isConfirming: boolean;
}

export const DeepResearchModal: React.FC<DeepResearchModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    initialBriefing,
    isConfirming,
}) => {
    const [briefing, setBriefing] = useState('');
    const [useWebSearch, setUseWebSearch] = useState(false);
    const setSparkUserInteractionBlockingRefresh = useAtlasStore(
        (state: AtlasState) => state.setSparkUserInteractionBlockingRefresh
    );

    // Reset briefing and web search when modal opens or initial briefing changes
    useEffect(() => {
        if (isOpen) {
            setBriefing(initialBriefing);
            setUseWebSearch(false);
        }
    }, [isOpen, initialBriefing]);

    // Block background refresh while modal is open
    useEffect(() => {
        if (isOpen) {
            setSparkUserInteractionBlockingRefresh(true);
            return () => setSparkUserInteractionBlockingRefresh(false);
        }
    }, [isOpen, setSparkUserInteractionBlockingRefresh]);

    const handleConfirm = async () => {
        if (briefing.trim().length === 0) {
            return;
        }
        try {
            await onConfirm(briefing.trim(), useWebSearch);
            onClose();
        } catch (error) {
            console.error('DeepSights research failed:', error);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            if (!isSubmitDisabled) {
                handleConfirm();
            }
        }
    };

    const isSubmitDisabled = isConfirming || briefing.trim().length === 0;

    if (!isOpen) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerTitleGroup}>
                        <h2 className={styles.modalTitle}>Run Desk Research</h2>
                        <p className={styles.modalSubtitle}>
                            Provide the briefing for the desk research agent.
                        </p>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close"
                        disabled={isConfirming}
                    >
                        <CloseIcon />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.formGroup}>
                        {/* Warning box - briefing is always required */}
                        <div className={styles.warningBox}>
                            <WarningAmberIcon fontSize="small" />
                            <span>DeepSights needs your specific guidance for this task.</span>
                        </div>
                        <textarea
                            id="briefing"
                            className={styles.textarea}
                            value={briefing}
                            onChange={(e) => setBriefing(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter research briefing..."
                            disabled={isConfirming}
                        />
                        <button
                            type="button"
                            className={`${styles.webSearchToggle} ${useWebSearch ? styles.webSearchToggleActive : ''}`}
                            onClick={() => setUseWebSearch(!useWebSearch)}
                            title="Include web resources to supplement the research"
                            disabled={isConfirming}
                        >
                            <PublicIcon fontSize="small" className={styles.webSearchIcon} />
                            Web Search
                        </button>
                    </div>
                </div>
                <div className={styles.modalFooter}>
                    <button
                        className={styles.confirmButton}
                        onClick={handleConfirm}
                        disabled={isSubmitDisabled}
                    >
                        {isConfirming ? (
                            <>
                                <AutorenewIcon className={styles.loadingSpinner} fontSize="small" />
                                Processing...
                            </>
                        ) : (
                            'Start'
                        )}
                    </button>
                    <button
                        className={styles.cancelButton}
                        onClick={onClose}
                        disabled={isConfirming}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DeepResearchModal; 
